import type { SiteDocument } from '../../types/siteRag';
import type { EvidenceSection, SiteEvidenceDocument } from '../../types/siteEvidence';
import { attributionFromSegment, formatAttribution } from './evidenceAttribution';
import { flattenLeafValues } from './docConflicts';

/** Compact navigation aid, never a replacement for the cited source or an authoritative fact store. */
export interface DocumentEvidenceDigest {
  version: '1';
  text: string;
  topicCount: number;
  attributedTopicCount: number;
  unmappedTopicCount: number;
  visualReadingCount: number;
  attachmentCount: number;
  warnings: string[];
}

export interface DocumentEvidenceQuality {
  documents: number;
  withDigest: number;
  attributedTopics: number;
  unmappedTopics: number;
  numericDocFacts: number;
  attributedNumericDocFacts: number;
  unresolvedDocumentConflicts: number;
  missingDigests: string[];
}

export function buildDocumentEvidenceDigest(doc: SiteDocument): DocumentEvidenceDigest | undefined {
  const segments = doc.topicSegments ?? [];
  const readings = doc.visualReadings ?? [];
  if (!doc.summary?.trim() && !segments.length && !readings.length) return undefined;
  const warnings: string[] = [];
  if (doc.topics?.coverage?.windows.some(w => w.status === 'failed')) warnings.push('Some document content windows could not be indexed; retained source text is available and the failed windows can be retried.');
  if (doc.topics?.coverage?.segmentsTruncated) warnings.push('The topic index reached its segment limit; consult the retained full source text for further details.');
  if (doc.truncated) warnings.push('Document exceeded the extraction ceiling; retained evidence is incomplete.');
  if (segments.some(s => s.attribution?.appliesToProject === 'other-project')) warnings.push('Includes statements prepared for another project; preserve their scope.');
  if ((doc.emailAttachments ?? []).some(a => a.status !== 'ingested')) warnings.push('Some email attachments were not ingested; inspect attachment outcomes.');
  if (readings.some(r => r.transcriptionIncomplete)) warnings.push('Some scanned-page text was unreadable or exceeded the per-page transcription limit; the source read is incomplete.');
  if (readings.length) warnings.push('Visual observations are screening evidence, with no inferred service or approval.');
  if (doc.visualCoverage?.unreadPages.length) warnings.push(`Visual evidence remains unread on pages ${doc.visualCoverage.unreadPages.join(', ')}; this pass reads at most ${doc.visualCoverage.limit} candidate pages. Split and upload those pages for further reading.`);
  const lines = [doc.summary?.slice(0, 500) ?? '', ...segments.slice(0, 12).map(s => {
    const attribution = formatAttribution(attributionFromSegment(s.attribution));
    return `${s.topic}${s.page ? ` (p.${s.page})` : ''}: ${s.summary}${attribution ? ` [${attribution}]` : ''}`;
  }), ...readings.slice(0, 4).map(r => `Visual${r.page ? ` p.${r.page}` : ''}: ${r.narrative}`)].filter(Boolean);
  return {
    version: '1', text: lines.join('\n').slice(0, 3500), topicCount: doc.topics?.segmentCount ?? segments.length,
    attributedTopicCount: segments.filter(s => attributionFromSegment(s.attribution)).length,
    unmappedTopicCount: segments.filter(s => !s.sedSections.length).length,
    visualReadingCount: doc.visualReadingCount ?? readings.length,
    attachmentCount: doc.emailAttachments?.length ?? 0,
    warnings,
  };
}

/** Reporting diagnostics only: deliberately independent of composite or evidence-quality pillar scoring. */
export function computeDocumentEvidenceQuality(sed: SiteEvidenceDocument, docs: readonly SiteDocument[]): DocumentEvidenceQuality {
  const digests = docs.map(d => d.evidenceDigest ?? buildDocumentEvidenceDigest(d));
  let numericDocFacts = 0;
  let attributedNumericDocFacts = 0;
  let unresolvedDocumentConflicts = 0;
  for (const value of Object.values(sed)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const slot = value as EvidenceSection<unknown>;
    unresolvedDocumentConflicts += slot.docConflicts?.filter(c => c.status === 'unresolved').length ?? 0;
    if (slot.provenance !== 'doc' && slot.status !== 'verified-from-doc') continue;
    const count = Object.values(flattenLeafValues(slot.data)).filter(v => typeof v === 'number').length;
    numericDocFacts += count;
    if (slot.provenanceDetail?.attribution || slot.linkedDocumentRefs?.some(r => r.attribution)) attributedNumericDocFacts += count;
  }
  return {
    documents: docs.length, withDigest: digests.filter(Boolean).length,
    attributedTopics: digests.reduce((n, d) => n + (d?.attributedTopicCount ?? 0), 0),
    unmappedTopics: digests.reduce((n, d) => n + (d?.unmappedTopicCount ?? 0), 0),
    numericDocFacts, attributedNumericDocFacts, unresolvedDocumentConflicts,
    missingDigests: docs.filter((_, i) => !digests[i]).map(d => d.fileName),
  };
}

/** Reject ambiguous numeric dates instead of guessing US/European month order. */
export function statedDateForEvidence(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const iso = /^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(value.trim());
  const namedMonth = /\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\b/i.test(value);
  if (!iso && !namedMonth) return undefined;
  const date = new Date(iso ? value.slice(0, 10) : /(?:GMT|UTC|[+-]\d{4})\b/.test(value) ? value : `${value} UTC`);
  if (!Number.isFinite(date.getTime())) return undefined;
  const day = date.toISOString().slice(0, 10);
  if (iso && day !== value.slice(0, 10)) return undefined;
  return day;
}
